/**
 * One German name per role and what each role may and may not do, for every
 * place the admin dashboard shows or assigns roles (users list and details,
 * IAM, tenant memberships). The rights sentences describe what the server
 * enforces (policies/active/authz.rego, api-gateway routes, compliance scanner);
 * change one only together with the rule behind it. The portal has the same
 * glossary in frontend/src/lib/userRoles.ts.
 */

export interface RoleText {
  key: string;
  fallback: string;
}

export interface RoleInfo {
  label: RoleText;
  summary?: RoleText;
  may?: RoleText;
  mayNot?: RoleText;
}

const info = (slug: string, label: string, summary?: string, may?: string, mayNot?: string): RoleInfo => ({
  label: { key: `users.roles.${slug}`, fallback: label },
  ...(summary ? { summary: { key: `users.roleInfo.${slug}.summary`, fallback: summary } } : {}),
  ...(may ? { may: { key: `users.roleInfo.${slug}.may`, fallback: may } } : {}),
  ...(mayNot ? { mayNot: { key: `users.roleInfo.${slug}.mayNot`, fallback: mayNot } } : {}),
});

const TENANT_ADMIN = info(
  'tenantAdmin',
  'Administrator/in',
  'Verwaltet den eigenen Mandanten: Mitglieder, Rollen, Einstellungen und Module.',
  'Mitglieder anlegen, deaktivieren und löschen, Rollen vergeben, Einstellungen, Module, Agenten und API-Schlüssel verwalten, Nachweise prüfen, Risiken akzeptieren, Freigaben entscheiden und das Audit-Log einsehen.',
  'Plattformweite Einstellungen oder andere Mandanten ändern.',
);

const ROLE_INFO: Record<string, RoleInfo> = {
  superadmin: info(
    'superadmin',
    'Superadmin',
    'Betreibt die Plattform: alle Mandanten, Nutzer und Einstellungen.',
    'Alles plattformweit, auch Superadmin-Rechte vergeben.',
    'In einem fremden Mandanten dessen Compliance-Entscheidungen treffen (dort nur lesend).',
  ),
  'platform-admin': info(
    'platformAdmin',
    'Plattform-Administrator/in',
    'Bedient Betriebsfunktionen der Plattform, ohne Superadmin zu sein.',
    'Plattformfunktionen wie die Flottensteuerung bedienen und im eigenen Mandanten Rollen bis Administrator/in vergeben.',
    'Superadmin-Rechte vergeben oder Mandanten außerhalb des eigenen verwalten.',
  ),
  tenant_admin: TENANT_ADMIN,
  // Self-signup hands the first person of a tenant `admin`; the server treats it
  // exactly like tenant_admin, so it is the same role here.
  admin: { ...TENANT_ADMIN, label: { key: 'users.roles.admin', fallback: 'Administrator/in' } },
  compliance_officer: info(
    'complianceOfficer',
    'Compliance-Verantwortliche/r',
    'Steuert die Compliance-Arbeit: Bewertungen, Geltungsbereich, Maßnahmen und Nachweisprüfung.',
    'Alles, was ein Mitglied darf, zusätzlich im Compliance-Scanner den Geltungsbereich festlegen, Ergebnisse und Nachweise prüfen (eigene nur mit dokumentierter Begründung), die Relevanz von Regelwerken mit Begründung einschätzen und Berichte erstellen.',
    'Risiken akzeptieren, Kontrollen bestätigen, Mitglieder, Rollen oder Einstellungen verwalten.',
  ),
  auditor: info(
    'auditor',
    'Prüfer/in',
    'Prüft lesend: Compliance-Ergebnisse, Nachweise, Maßnahmen und Audit-Log.',
    'Compliance-Ergebnisse, Nachweise, Maßnahmen und Dokumente lesen, das Audit-Log einsehen, Prüfberichte erzeugen und herunterladen sowie das Audit-Paket des Compliance-Scanners herunterladen.',
    'Dokumente, Compliance-Daten oder Einstellungen anlegen, ändern oder löschen, Freigaben entscheiden, Mitglieder verwalten.',
  ),
  management: info(
    'management',
    'Geschäftsführung',
    'Behält die Lage im Blick und entscheidet Freigaben.',
    'Lage, Compliance, Regulatorik, Cockpit, Aktionscenter und Dokumente lesen, Freigabeanfragen genehmigen oder ablehnen, Berichte erstellen und das Audit-Paket des Compliance-Scanners herunterladen.',
    'Dokumente, Compliance-Daten oder Einstellungen ändern, Mitglieder verwalten, das Audit-Log aller Aktivitäten einsehen.',
  ),
  user: info(
    'user',
    'Mitglied',
    'Arbeitet mit Agenten, Dokumenten und Compliance-Aufgaben.',
    'Agenten und Chat nutzen, Dokumente hochladen, Compliance-Bewertungen starten, Maßnahmen bearbeiten und Nachweise hochladen.',
    'Nachweise prüfen, Mitglieder, Rollen oder Einstellungen verwalten, das Audit-Log einsehen.',
  ),
  viewer: info(
    'viewer',
    'Betrachter/in',
    'Liest Inhalte und Ergebnisse, ändert nichts.',
    'Inhalte, Dokumente und Compliance-Ergebnisse lesen, die für die Rolle sichtbar sind.',
    'Dokumente, Compliance-Daten oder Einstellungen anlegen, ändern oder löschen, Berichte erstellen, Mitglieder verwalten.',
  ),
  agent_developer: info(
    'agentDeveloper',
    'Agenten-Entwickler/in',
    'Arbeitet wie ein Mitglied und pflegt zusätzlich Wissens- und Lerndaten.',
    'Alles, was ein Mitglied darf, zusätzlich Wissens- und Lerndaten der Plattform bearbeiten.',
    'Agenten anlegen oder ändern, Mitglieder oder Einstellungen verwalten.',
  ),
  analyst: info(
    'analyst',
    'Analyst/in',
    'Liest Inhalte und Auswertungen, ändert nichts.',
    'Inhalte, Auswertungen und Compliance-Ergebnisse lesen.',
    'Dokumente, Compliance-Daten oder Einstellungen anlegen, ändern oder löschen.',
  ),
  partner: info(
    'partner',
    'Partner',
    'Lesender Zugang für Partner, ändert nichts.',
    'Inhalte und Compliance-Ergebnisse lesen, die für die Rolle sichtbar sind.',
    'Dokumente, Compliance-Daten oder Einstellungen anlegen, ändern oder löschen.',
  ),
};

const ALIASES: Record<string, string> = {
  superuser: 'superadmin',
  'super-admin': 'superadmin',
  super_admin: 'superadmin',
  'platform-superadmin': 'superadmin',
  platform_superadmin: 'superadmin',
  platform_admin: 'platform-admin',
  'tenant-admin': 'tenant_admin',
  'compliance-officer': 'compliance_officer',
  'agent-developer': 'agent_developer',
  developer: 'agent_developer',
};

export function canonicalRole(role?: string | null): string {
  const normalized = (role || '').trim().toLowerCase();
  return ALIASES[normalized] ?? normalized;
}

export function roleInfo(role?: string | null): RoleInfo | null {
  return ROLE_INFO[canonicalRole(role)] ?? null;
}

type TranslateFn = (key: string, fallback: string) => string;

export function roleLabel(t: TranslateFn, role?: string | null): string {
  const found = roleInfo(role);
  if (found) return t(found.label.key, found.label.fallback);
  return (role || '').replace(/[_-]+/g, ' ').trim() || '—';
}

/** Roles whose holder administers a tenant (or the platform): handing one out asks first. */
export function isAdminLevelRole(role?: string | null): boolean {
  return ['tenant_admin', 'admin', 'superadmin', 'platform-admin'].includes(canonicalRole(role));
}

// ---------------------------------------------------------------------------
// One status model for people: Eingeladen, Aktiv, Deaktiviert (+ the IAM's
// temporary lock after failed sign-ins).
// ---------------------------------------------------------------------------

export type PersonStatus = 'invited' | 'active' | 'deactivated' | 'locked';

export const STATUS_TEXT: Record<PersonStatus, RoleText> = {
  invited: { key: 'users.status.invited', fallback: 'Eingeladen' },
  active: { key: 'users.status.active', fallback: 'Aktiv' },
  deactivated: { key: 'users.status.deactivated', fallback: 'Deaktiviert' },
  locked: { key: 'users.status.locked', fallback: 'Gesperrt' },
};

/**
 * Status of an account from the users routes: Deaktiviert when is_active is
 * false or the IAM marked it 'disabled' (deactivated in the IAM console),
 * Gesperrt while the IAM lock holds, Eingeladen while the account exists but
 * the person has neither set a password nor signed in (the api-gateway returns
 * status, password_set and last_login), else Aktiv. Same rule as ?status=.
 */
export function personStatusOf(user: {
  is_active?: boolean | null;
  status?: string | null;
  password_set?: boolean | null;
  last_login?: string | null;
}): PersonStatus {
  const iam = (user.status || '').trim().toLowerCase();
  if (user.is_active === false || iam === 'disabled') return 'deactivated';
  if (iam === 'locked') return 'locked';
  if (user.password_set === false && !user.last_login) return 'invited';
  return 'active';
}

/**
 * The IAM's account status (active, locked, disabled, invited,
 * pending_verification). The users routes deactivate through is_active alone,
 * so is_active=false wins over a status the IAM still keeps as "active".
 */
export function personStatusFromIam(status?: string | null, isActive?: boolean | null): PersonStatus {
  if (isActive === false) return 'deactivated';
  switch ((status || '').trim().toLowerCase()) {
    case 'locked':
      return 'locked';
    case 'disabled':
      return 'deactivated';
    case 'invited':
    case 'pending_verification':
      return 'invited';
    default:
      return 'active';
  }
}
